import { Music, Building2 } from 'lucide-react'
import type { StepProps } from '@/types/apply'

export default function StepRole({ formData, updateField, errors, clearError }: StepProps) {
  const r = formData.role_info

  function selectRole(role: 'artist' | 'label') {
    updateField('role_info', 'role', role)
    // Clear the other role's fields so a switch can't leave stray data
    // behind — role_info is one flat object, so without this an artist_name
    // typed before switching to Label (or vice versa) would still be sitting
    // in state, and applications_controller.rb#create stores whatever it's
    // handed. Backend scrubs the same way as defense in depth (a restored
    // localStorage draft or a WebMCP prefill can still set both).
    if (role === 'artist') {
      updateField('role_info', 'label_name', '')
      updateField('role_info', 'artists_under_management', '')
    } else {
      updateField('role_info', 'artist_name', '')
    }
    clearError('role')
  }

  return (
    <div className="space-y-6">
      {errors.role && <p className="apply-error-text">{errors.role}</p>}

      <div className="grid gap-4 sm:grid-cols-2">
        <button
          type="button"
          onClick={() => selectRole('artist')}
          className={`apply-card-selectable ${r.role === 'artist' ? 'is-selected' : ''}`}
        >
          <Music className="h-8 w-8" />
          <div className="mt-3 text-lg font-semibold">I'm an Artist</div>
          <p className="mt-1 text-sm text-white/50">Solo artist or band looking to distribute music</p>
        </button>

        <button
          type="button"
          onClick={() => selectRole('label')}
          className={`apply-card-selectable ${r.role === 'label' ? 'is-selected' : ''}`}
        >
          <Building2 className="h-8 w-8" />
          <div className="mt-3 text-lg font-semibold">I'm a Label</div>
          <p className="mt-1 text-sm text-white/50">Record label managing one or more artists</p>
        </button>
      </div>

      {r.role === 'artist' && (
        <div>
          <label className="apply-label">Artist / Band Name</label>
          <input
            type="text"
            className={`apply-input ${errors.artist_name ? 'apply-input-error' : ''}`}
            placeholder="Your artist or band name"
            value={r.artist_name}
            onChange={e => { updateField('role_info', 'artist_name', e.target.value); clearError('artist_name') }}
            autoFocus
          />
          {errors.artist_name && <p className="apply-error-text">{errors.artist_name}</p>}
        </div>
      )}

      {r.role === 'label' && (
        <div className="space-y-5">
          <div>
            <label className="apply-label">Label Name</label>
            <input
              type="text"
              className={`apply-input ${errors.label_name ? 'apply-input-error' : ''}`}
              placeholder="Your label name"
              value={r.label_name}
              onChange={e => { updateField('role_info', 'label_name', e.target.value); clearError('label_name') }}
              autoFocus
            />
            {errors.label_name && <p className="apply-error-text">{errors.label_name}</p>}
          </div>
          <div>
            <label className="apply-label">Artists Under Management</label>
            <textarea
              className={`apply-input min-h-[96px] resize-y ${errors.artists_under_management ? 'apply-input-error' : ''}`}
              placeholder={`List each artist your label manages — one per line, with a Spotify or Instagram link in brackets. For example:\nJane Doe (https://open.spotify.com/artist/…)\nThe Midnight Band (https://instagram.com/themidnightband)`}
              value={r.artists_under_management}
              onChange={e => { updateField('role_info', 'artists_under_management', e.target.value); clearError('artists_under_management') }}
            />
            <p className="mt-1.5 text-sm text-white/50">
              Add the artist name and a Spotify or Instagram link in brackets so we can identify each artist.
            </p>
            {errors.artists_under_management && <p className="apply-error-text">{errors.artists_under_management}</p>}
          </div>
        </div>
      )}
    </div>
  )
}
